import { createElement } from '../../core/dom/helpers.js';
import { formBuilderDriver } from '../../components/form-field/drivers/form-builder-driver.js';
import {
  parseStructure, serializeStructure, isEmptyForm, isInvalidFieldset,
} from '../../core/form-builder/form-structure.js';
import { isAbortError } from '../../core/http/is-abort-error.js';
import { trackVisualViewportHeight } from '../../core/ui/visual-viewport-height.js';

/**
 * @typedef {Object} FormBuilderModalOptions
 * @property {import('../../contracts/api.js').ApiClient} apiClient - App-bound CRUD client.
 * @property {string | number} [copyFromId] - Seed the editor by copying an existing form's structure.
 * @property {AbortSignal} [signal] - External abort (host field destroyed) → close + resolve null.
 * @property {{ debug: Function, info: Function, warn: Function, error: Function }} [logger]
 */

const SUBMIT_OPTIONS = [
  { value: '', label: 'Select type of form' },
  { value: 'submit_form.php', label: 'Contact form' },
  { value: 'test.php', label: 'Query form' },
];

const META_FIELDS = [
  { key: 'form_name', label: 'Form name', required: true },
  { key: 'form_subject', label: 'Email subject', required: false },
  { key: 'mail', label: 'Recipient email', required: false },
  { key: 'mail_cc', label: 'CC (semicolon-separated)', required: false },
  { key: 'mail_bcc', label: 'BCC (semicolon-separated)', required: false },
  { key: 'client', label: 'Client', required: false },
];

/**
 * Open the create / copy-from form-builder modal. Parity with `file-picker-modal.js`
 * (now in `features/file-browser/`): app-bound (apiClient arrives as a bound arg, never
 * imported by the driver), and returns `{ promise, destroy }` like `FilePicker.open`.
 * This modal stays in `pages/` — it is single-consumer (fb-forms), not a reused feature.
 * Resolves the created form's `{ id, form_name }`, or `null` on cancel/Escape/abort.
 *
 * @param {FormBuilderModalOptions} opts
 * @returns {{ promise: Promise<{ id: string | number, form_name: string } | null>, destroy: () => void }}
 */
export function openFormBuilderModal(opts) {
  const { apiClient, copyFromId, signal } = opts;

  let resolveOuter = /** @type {(v: { id: string | number, form_name: string } | null) => void} */ (() => {});
  const promise = new Promise((resolve) => { resolveOuter = resolve; });

  let closed = false;
  const internalAbort = new AbortController();
  /** Monotonic copy-from request id so a slow earlier fetch can't overwrite a newer pick. */
  let copyVersion = 0;

  // --- Overlay + panel ---
  const overlay = createElement('div', { class: 'fb-modal__overlay' });
  const panel = createElement('div', {
    class: 'fb-modal__panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Create form',
  });

  const header = createElement('div', { class: 'fb-modal__header' });
  const title = createElement('h3', { class: 'fb-modal__title' }, ['Create new form']);
  const closeBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'fb-modal__close', 'aria-label': 'Close',
  }, ['×']));
  header.appendChild(title);
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const body = createElement('div', { class: 'fb-modal__body' });
  panel.appendChild(body);

  // Form controls reuse the app's standard form-field classes (.form-field / -label / -input)
  // so they look identical to fields everywhere else in the system.

  // --- Copy-from existing ---
  const copyRow = createElement('div', { class: 'form-field' });
  const copyLabel = createElement('label', { class: 'form-field-label', for: 'fb-modal-copy' }, ['Copy from existing form']);
  const copySelect = /** @type {HTMLSelectElement} */ (createElement('select', { id: 'fb-modal-copy', class: 'form-field-input' }, [
    /** @type {HTMLOptionElement} */ (createElement('option', { value: '' }, ['— none —'])),
  ]));
  copyRow.appendChild(copyLabel);
  copyRow.appendChild(copySelect);
  body.appendChild(copyRow);

  // --- Metadata inputs ---
  /** @type {Record<string, HTMLInputElement>} */
  const metaInputs = {};
  for (const f of META_FIELDS) {
    const row = createElement('div', { class: 'form-field' });
    const id = `fb-modal-${f.key}`;
    const label = createElement('label', { class: 'form-field-label', for: id }, [f.label + (f.required ? ' *' : '')]);
    const input = /** @type {HTMLInputElement} */ (createElement('input', { type: 'text', id, class: 'form-field-input' }));
    if (f.required) input.setAttribute('aria-required', 'true');
    metaInputs[f.key] = input;
    row.appendChild(label);
    row.appendChild(input);
    body.appendChild(row);
  }

  // submit_script select
  const ssRow = createElement('div', { class: 'form-field' });
  const ssLabel = createElement('label', { class: 'form-field-label', for: 'fb-modal-submit' }, ['Form type']);
  const submitSelect = /** @type {HTMLSelectElement} */ (createElement('select', { id: 'fb-modal-submit', class: 'form-field-input' },
    SUBMIT_OPTIONS.map((o) => {
      const opt = /** @type {HTMLOptionElement} */ (createElement('option', { value: o.value }, [o.label]));
      if (o.value === 'submit_form.php') opt.selected = true;
      return opt;
    })));
  ssRow.appendChild(ssLabel);
  ssRow.appendChild(submitSelect);
  body.appendChild(ssRow);

  // --- Embedded form-builder editor ---
  const builderLabel = createElement('div', { class: 'form-field-label' }, ['Form fields']);
  const builderHost = createElement('div', { class: 'fb-modal__builder' });
  body.appendChild(builderLabel);
  body.appendChild(builderHost);

  /** @type {import('../../contracts/forms.js').FormBuilderFieldset[]} */
  let structure = [];
  const builderHandle = formBuilderDriver.mount(builderHost, /** @type {*} */ ({
    def: { type: 'form-builder', key: 'form_structure', label: 'Form fields' },
    value: structure,
    onChange: (/** @type {unknown} */ v) => { structure = /** @type {*} */ (v); },
    setErrors: () => {},
  }));

  // --- Footer: status + actions ---
  const footer = createElement('div', { class: 'fb-modal__footer' });
  const status = createElement('div', { class: 'fb-modal__status', role: 'alert' });
  const cancelBtn = /** @type {HTMLButtonElement} */ (createElement('button', { type: 'button', class: 'fb-modal__cancel btn btn-secondary' }, ['Cancel']));
  const saveBtn = /** @type {HTMLButtonElement} */ (createElement('button', { type: 'button', class: 'fb-modal__save btn btn-primary' }, ['Save form']));
  footer.appendChild(status);
  footer.appendChild(cancelBtn);
  footer.appendChild(saveBtn);
  panel.appendChild(footer);

  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  // On a phone the full-screen overlay is fixed to the LAYOUT viewport, which
  // the on-screen keyboard does not shrink (and iOS pans the visual viewport),
  // so the footer hides behind the keyboard and the body can't free-scroll to
  // the end of the form. Pin the overlay to the VISIBLE slice while open so its
  // scrollable body reaches the end. No-op on desktop / engines without the API.
  const isMobileWidth = typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 767px)').matches;
  const vvHandle = isMobileWidth
    ? trackVisualViewportHeight(overlay, { signal: internalAbort.signal })
    : null;

  // --- Teardown ---
  function onExternalAbort() { cancel(); }
  if (signal) signal.addEventListener('abort', onExternalAbort);

  function cleanup() {
    if (closed) return;
    closed = true;
    internalAbort.abort();
    if (signal) signal.removeEventListener('abort', onExternalAbort);
    document.removeEventListener('keydown', handleKeydown, true);
    if (vvHandle) vvHandle.destroy();
    builderHandle.destroy();
    overlay.remove();
  }
  function cancel() {
    if (closed) return;
    cleanup();
    resolveOuter(null);
  }

  /** @param {KeyboardEvent} e */
  function handleKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // shield the parent crud-editor's own Escape handler
      cancel();
      return;
    }
    if (e.key === 'Tab') {
      const focusable = /** @type {HTMLElement[]} */ (
        [...panel.querySelectorAll('input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)')]
      );
      if (focusable.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const idx = focusable.indexOf(/** @type {HTMLElement} */ (document.activeElement));
      const next = e.shiftKey
        ? (idx <= 0 ? focusable.length - 1 : idx - 1)
        : (idx >= focusable.length - 1 ? 0 : idx + 1);
      focusable[next].focus();
    }
  }
  document.addEventListener('keydown', handleKeydown, true);
  closeBtn.addEventListener('click', cancel);
  cancelBtn.addEventListener('click', cancel);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) cancel(); });

  // --- Copy-from population + handling ---
  /** Seed the editor from an existing form's structure (race-guarded). @param {string} id */
  function loadCopySource(id) {
    // Bump the version FIRST so switching back to "none" (empty id) invalidates an in-flight
    // fetch — otherwise a slow earlier source could still seed the editor after "none" is picked.
    const thisVersion = ++copyVersion;
    if (!id) return;
    apiClient.get(`/fb-savedforms/${encodeURIComponent(id)}`, { signal: internalAbort.signal })
      .then((row) => {
        if (closed || thisVersion !== copyVersion) return; // a newer pick superseded this fetch
        const seeded = parseStructure(/** @type {*} */ (row)?.form_structure);
        structure = seeded;
        builderHandle.setValue(seeded); // silent — no spurious onChange
      })
      .catch((err) => { if (!isAbortError(err)) status.textContent = 'Could not load the selected form.'; });
  }
  copySelect.addEventListener('change', () => loadCopySource(copySelect.value));

  // Populate the copy-from list (and optionally pre-seed from copyFromId).
  apiClient.get('/fb-savedforms/options', { signal: internalAbort.signal })
    .then((res) => {
      if (closed) return;
      const options = Array.isArray(res) ? res : (/** @type {*} */ (res)?.options ?? []);
      for (const o of options) {
        if (!o || o.value == null || o.label === '') continue;
        const opt = /** @type {HTMLOptionElement} */ (createElement('option', { value: String(o.value) }, [String(o.label)]));
        if (copyFromId != null && String(o.value) === String(copyFromId)) opt.selected = true;
        copySelect.appendChild(opt);
      }
      if (copyFromId != null) loadCopySource(String(copyFromId));
    })
    .catch(() => { /* non-fatal — copy-from simply unavailable */ });

  // --- Save ---
  /** Trimmed metadata value. @param {string} key */
  const metaVal = (key) => metaInputs[key].value.trim();

  async function save() {
    if (closed) return;
    status.textContent = '';

    // (a) name required
    const formName = metaVal('form_name');
    if (formName === '') {
      status.textContent = 'Form name is required.';
      metaInputs.form_name.focus();
      return;
    }
    // (b) + (c) structure validity (shared predicates)
    if (isEmptyForm(structure)) {
      status.textContent = 'Add at least one field to the form.';
      builderHandle.setAriaInvalid?.(true);
      return;
    }
    if (structure.some(isInvalidFieldset)) {
      status.textContent = 'Each choice field needs both a title and at least one option.';
      builderHandle.setAriaInvalid?.(true);
      return;
    }
    builderHandle.setAriaInvalid?.(false); // structure is valid — clear the flag

    saveBtn.disabled = true;
    try {
      // duplicate-name pre-check: /options?q= WITHOUT limit (mock only caps when limit present),
      // then exact normalized compare. Global (mock has no country column).
      const dupRes = await apiClient.get(`/fb-savedforms/options?q=${encodeURIComponent(formName)}`, { signal: internalAbort.signal });
      const dupOptions = Array.isArray(dupRes) ? dupRes : (/** @type {*} */ (dupRes)?.options ?? []);
      const needle = formName.toLowerCase();
      if (dupOptions.some((/** @type {*} */ o) => String(o?.label ?? '').trim().toLowerCase() === needle)) {
        status.textContent = 'A form with this name already exists.';
        saveBtn.disabled = false;
        metaInputs.form_name.focus();
        return;
      }

      const payload = {
        form_name: formName,
        form_subject: metaVal('form_subject'),
        mail: metaVal('mail'),
        mail_cc: metaVal('mail_cc'),
        mail_bcc: metaVal('mail_bcc'),
        client: metaVal('client'),
        submit_script: submitSelect.value,
        form_structure: serializeStructure(structure),
      };
      const created = await apiClient.post('/fb-savedforms', payload, { signal: internalAbort.signal });
      if (closed) return;
      const id = /** @type {*} */ (created)?.id;
      cleanup();
      resolveOuter({ id, form_name: formName });
    } catch (err) {
      if (isAbortError(err)) return;
      status.textContent = 'Could not save the form. Please try again.';
      saveBtn.disabled = false;
    }
  }
  saveBtn.addEventListener('click', () => { save(); });

  // Focus the first input.
  setTimeout(() => { if (!closed) metaInputs.form_name.focus(); }, 0);

  return { promise, destroy: cancel };
}
